import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';
import {
  compose,
  TypographyProps,
  typography,
  SpaceProps,
  space,
  variant,
  border,
  BorderProps,
  BackgroundProps,
  background,
  theme,
} from '@lucid/theme';
import { AccordionItemPanel, AccordionItemState } from 'react-accessible-accordion';
import gsap from 'gsap';

import { Component } from '../../types/types';
import { Box } from '../../components/box/box';

export interface AccordionPanelProps extends BackgroundProps, TypographyProps, SpaceProps, BorderProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The children to be rendered with the Accordion.Panel. */
  children?: ReactNode;
  /** The available variants for the `Accordion.Panel` component. */
  variant?: Component.Type['Accordion']['Panel'];
}

const panelStyleProps = compose(background, typography, space, border);

const StyledAccordionPanel = styled(AccordionItemPanel)<AccordionPanelProps>(
  variant({
    scale: 'accordion.panel',
    variants: {
      defaultVariant: {},
    },
  }),
  {
    label: 'panel',
  },
  panelStyleProps,
);

const AccordionPanel: FC<AccordionPanelProps> = ({ className, variant = 'default', ...rest }) => {
  const animationRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const openAnimation = useRef<any>();
  const closeAnimation = useRef<any>();

  useEffect(() => {
    if (animationRef.current) {
      openAnimation.current = gsap
        .timeline({
          paused: true,
          defaults: {
            ease: theme.beziers.slowStartEase,
          },
        })
        .set(animationRef.current, { display: 'block' })
        .to(animationRef.current, {
          height: 'auto',
          duration: 0.633,
          delay: 0.05,
        })
        .to(animationRef.current, { y: 0, opacity: 1, duration: 0.35 }, '-=0.05');
      closeAnimation.current = gsap
        .timeline({
          paused: true,
          defaults: {
            ease: theme.beziers.slowStartEase,
          },
        })
        .to(animationRef.current, { opacity: 0, duration: 0.15 }, 0)
        .to(animationRef.current, { y: '-80px', duration: 0.2 }, 0)
        .to(animationRef.current, { height: 0, duration: 0.35, delay: 0.05 })
        .set(animationRef.current, { display: 'none' });
    }
  });

  useEffect(() => {
    if (animationRef.current && openAnimation.current && closeAnimation.current) {
      isExpanded ? openAnimation.current.play() : closeAnimation.current.play();
    }
  }, [isExpanded]);

  return (
    <AccordionItemState>
      {state => {
        setIsExpanded(state.expanded || false);
        return (
          <Box ref={animationRef} css={{ opacity: 0, height: 0 }}>
            <StyledAccordionPanel
              {...rest}
              css={{ background: 'transparent', display: 'block' }}
              // eslint-disable-next-line react/jsx-sort-props
              className={className}
              variant={variant}
            />
          </Box>
        );
      }}
    </AccordionItemState>
  );
};

export { AccordionPanel };
export default AccordionPanel;
